import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, LinearProgress } from '@mui/material';
import validateImage from '../../utils/Image-validation';
import { getSignedUrl, uploadFileToS3 } from '../../services/videos.service';
import { getCurrentTimestamp } from '../../utils/datetime.util';

const S3ImageUpload = ({
  value,
  updateImage,
  s3Folder,
  aspectRatio,
  allowedSize,
  setSnackbarInfo,
  showAsPerOpacity,
  opacity,
  isPackage,
  isSplash,
  splashImageSuccess,
  isLG,
  viewMode,
}) => {
  const hiddenFileInput = useRef(null);
  const [imageURL, setImageURL] = useState(value);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  useEffect(() => {
    setImageURL(value);
  }, [value]);

  let allowedTypes = '.png,.jpg,.jpeg,.gif,.ico';
  let successMessage = 'Image uploaded successfully.';

  if (isLG) {
    allowedTypes = '.zip,file';
    successMessage = 'LG ZIP file uploaded successfully.';
  } else if (isPackage) {
    allowedTypes = '.pkg,file';
    successMessage = 'Roku package uploaded successfully.';
  } else if (isSplash) {
    allowedTypes = '.png';
  }
  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  const handleFileChange = (event) => {
    setSnackbarInfo({
      show: false,
    });
    setBtnDisabled(true);
    setUploadProgress(1);
    const selectedFile = event.target.files[0];

    if (!selectedFile) {
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'No file selected.',
        });
      }, 100);
      return;
    }

    const acceptedImages = [
      'image/jpeg',
      'image/gif',
      'image/png',
      'image/jpg',
      'image/vnd.microsoft.icon',
      'image/x-icon',
    ];

    if (isLG) {
      acceptedImages.push('application/zip');
      acceptedImages.push('application/x-zip-compressed');
      acceptedImages.push('multipart/x-zip');
      const fileName = selectedFile.name.toLowerCase();
      const allowFileName = s3Folder.split('/');
      if (fileName !== `${allowFileName[0].toLowerCase()}-webhosted.zip`) {
        setImageURL('');
        updateImage('');
        setBtnDisabled(false);
        setTimeout(() => {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: `Upload failed: The file name must be ${allowFileName[0]}-webhosted.zip. Please rename your file and try again.`,
          });
        }, 100);

        hiddenFileInput.current.value = '';
        setUploadProgress(0);
        return;
      }
    }
    let isPkg = false;
    if (isPackage && selectedFile) {
      acceptedImages.push('application/x-xar');
      const fileName = selectedFile.name.toLowerCase();
      isPkg = fileName.endsWith('.pkg');
      if (!isPkg) {
        setTimeout(() => {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: 'Please select .pkg file only.',
          });
        }, 100);
        hiddenFileInput.current.value = '';
        return;
      }
    }

    if (!acceptedImages.includes(selectedFile.type) && !isPackage) {
      let ErrorMessage =
        'Please select an image of type jpeg, jpg, png, gif or icon.';
      if (isLG) {
        ErrorMessage = 'Please select .zip file only.';
      }
      setImageURL('');
      updateImage('');
      setBtnDisabled(false);
      setTimeout(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: ErrorMessage,
        });
      }, 100);
      hiddenFileInput.current.value = '';
      setUploadProgress(0);
      return;
    }

    validateImage(
      selectedFile,
      aspectRatio,
      allowedSize,
      isPackage,
      isPkg,
      isSplash,
      isLG
    )
      .then(async () => {
        const signedUrlData = await getSignedUrl({
          fileName: selectedFile.name,
          fileType: !isPkg ? selectedFile.type : 'application/x-xar',
          folder: s3Folder || 'uploads',
          isSplash,
        });

        if (!signedUrlData) {
          return;
        }

        try {
          await uploadFileToS3(
            selectedFile,
            signedUrlData.data.uploadUrl,
            (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setUploadProgress(progress);
            }
          );
          setBtnDisabled(false);
          setImageURL(signedUrlData.data.fileUrl);
          updateImage(signedUrlData.data.fileUrl);
          if (isSplash) {
            splashImageSuccess();
          }

          setSnackbarInfo({
            show: true,
            type: 'success',
            message: successMessage,
          });
          hiddenFileInput.current.value = '';
          setTimeout(() => {
            setUploadProgress(0);
          }, 250);
        } catch (error) {
          setImageURL('');
          updateImage('');
          setBtnDisabled(false);
          const message =
            error.response?.data?.message ||
            'Something went wrong, please try again.';
          setSnackbarInfo({
            show: true,
            type: 'error',
            message,
          });
          hiddenFileInput.current.value = '';
          setUploadProgress(0);
        }
      })
      .catch((err) => {
        setImageURL('');
        updateImage('');
        setBtnDisabled(false);
        setUploadProgress(0);
        const message =
          err?.message || 'Something went wrong, please try again.';
        setSnackbarInfo({
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const handleRemoveImage = () => {
    updateImage('');
    setImageURL('');
    hiddenFileInput.current.value = '';
  };

  return (
    <>
      <input
        type="file"
        accept={allowedTypes}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box
        spacing={1}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
        }}
      >
        {imageURL && !isPackage && !isLG && (
          <Box
            sx={{
              width: '100px',
              height: '100px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={`${imageURL}?q=${getCurrentTimestamp()}`}
              style={{
                maxHeight: '100px',
                maxWidth: '100px',
                objectFit: 'contain',
                opacity: showAsPerOpacity ? opacity : 1,
              }}
              alt="Uploaded"
            />
          </Box>
        )}
        {(isPackage || isLG) && (
          <Box
            sx={{
              height: '100px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {imageURL && imageURL.split('/')[imageURL.split('/').length - 1]}
          </Box>
        )}
        {!viewMode && (
          <Box
            item
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
            }}
          >
            <Button
              onClick={handleClick}
              variant="outlined"
              size="medium"
              disabled={btnDisabled}
            >
              Select File
            </Button>
            {uploadProgress > 0 && uploadProgress <= 100 && (
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '150px',
                  gap: 10,
                }}
              >
                <LinearProgress
                  style={{ width: '100%' }}
                  variant="determinate"
                  value={uploadProgress}
                />
                <p>{uploadProgress}%</p>
              </Box>
            )}
            {imageURL && (
              <Button
                onClick={handleRemoveImage}
                variant="outlined"
                size="medium"
              >
                Remove File
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

S3ImageUpload.propTypes = {
  value: PropTypes.string.isRequired,
  updateImage: PropTypes.func.isRequired,
  s3Folder: PropTypes.string,
  aspectRatio: PropTypes.number.isRequired,
  allowedSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSnackbarInfo: PropTypes.func.isRequired,
  showAsPerOpacity: PropTypes.bool,
  opacity: PropTypes.number,
  isSplash: PropTypes.bool,
  isPackage: PropTypes.bool,
  splashImageSuccess: PropTypes.func,
  isLG: PropTypes.bool,
  viewMode: PropTypes.bool,
};

S3ImageUpload.defaultProps = {
  s3Folder: 'partners',
  showAsPerOpacity: false,
  opacity: 100,
  isPackage: false,
  isSplash: false,
  isLG: false,
  splashImageSuccess: () => {},
  viewMode: false,
};

export default S3ImageUpload;
